import { useLayoutEffect, useRef } from "react";
import SkinLogo from "./skin-logo";

// An arrow into the small square Slowed shrinks to; as tall as the switch.
function MiniIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect
        x="2"
        y="9"
        width="7"
        height="7"
        rx="1.75"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.55"
      />
      <path
        d="M16 2 10.5 7.5M10.5 3.25V7.5h4.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// A minimize button, not a switch: every click shrinks Slowed into Mini
// Slowed. Kept mounted while Slowed is off so it can fold away.
function MiniButton({ hidden, onMinimize }) {
  return (
    <button
      type="button"
      className={`header__mini${hidden ? " header__mini--hidden" : ""}`}
      aria-hidden={hidden || undefined}
      tabIndex={hidden ? -1 : undefined}
      {...(hidden ? { inert: true } : {})}
      aria-label="Minimize to Mini Slowed (Beta)"
      onClick={() => onMinimize?.()}
    >
      <MiniIcon />
    </button>
  );
}

const UNLOCK_LABEL_BY_STEP = [
  "default",
  "trial-mode",
  "trial-days",
  "usage-reset",
  // Only cycles in once the dashboard's cap offer applies to this install.
  "promo",
];

function syncUnlockButtonWidth(button, step) {
  if (!button) return;
  const kind = UNLOCK_LABEL_BY_STEP[step] || "default";
  const active = button.querySelector(
    `.subscription-status-button__label--${kind}`,
  );
  if (!active) return;

  const styles = getComputedStyle(button);
  const pad =
    (parseFloat(styles.paddingLeft) || 0) +
    (parseFloat(styles.paddingRight) || 0);
  const next = Math.ceil(active.scrollWidth + pad);
  if (!Number.isFinite(next) || next <= 0) return;
  if (button.style.width === `${next}px`) return;
  button.style.width = `${next}px`;
}

export default function PopupHeader({
  announcement,
  isPro,
  trialMode,
  trialEnded,
  remainingTrialDays,
  usageHit,
  usageRemaining,
  usagePeriod,
  subscriptionStatusStep,
  // The dashboard's cap-triggered offer ({ label, afterCaps }) and whether
  // this install has hit enough weekly caps to qualify for it.
  usagePromo,
  promoQualified,
  musicProOnly,
  isSlowedEnabled,
  contentApplying,
  mount,
  billingLoading,
  upgradeError,
  onUpgradeClick,
  onManageSubscriptionClick,
  // Pro's button opens the Billing sheet; a host without one manages the
  // plan directly.
  onProClick,
  onDebugToggle,
  onSlowedToggle,
  onUsageClick,
  // Off YouTube (the gate) there is nothing to switch on: the header keeps
  // its plan button and the logo, without the switch or the bar under it.
  showSwitch = true,
  // Mini Slowed (Beta), content/panel/mini.js. Not on Safari.
  offersMini = false,
  onMiniMinimize,
}) {
  const unlockButtonRef = useRef(null);
  const hasTrial = trialMode && remainingTrialDays >= 0;
  const showUnlockButton = !isPro;
  const usageMinutesLeft = Math.max(
    0,
    Math.floor(Number(usageHit ? 0 : usageRemaining) || 0),
  );
  const usageText =
    usageMinutesLeft >= 60
      ? `${Math.floor(usageMinutesLeft / 60)}h ${usageMinutesLeft % 60}m left`
      : `${usageMinutesLeft} min left`;
  // Post-trial allowance: Unlock PRO → Usage mode → minutes left → Resets …,
  // including after the cap so 0 min left still cycles. Minutes left is the
  // smaller of the weekly and monthly windows, so name the window that is
  // actually holding it back — calling a monthly squeeze "weekly" made the
  // allowance look like a fraction of what it is.
  const usageResetText =
    usagePeriod === "monthly" ? "Resets monthly" : "Resets weekly";
  const hasUsage = !isPro && !hasTrial && trialEnded === true;
  const showUsageDepletedUnlock = hasUsage && usageHit === true;
  // The cap offer joins the rotation only for metered users who have hit
  // enough weekly caps to qualify. It never touches the free slow-down — it
  // only changes what the Unlock PRO button says and where it goes.
  const showPromo =
    hasUsage && promoQualified === true && Boolean(usagePromo?.label);
  const rotating = hasTrial || hasUsage;
  const maxStep = hasUsage ? (showPromo ? 4 : 3) : 2;
  const step = rotating ? Math.min(subscriptionStatusStep || 0, maxStep) : 0;
  const daysText =
    remainingTrialDays > 0
      ? `${remainingTrialDays} ${remainingTrialDays === 1 ? "day" : "days"} left`
      : "Last day";
  const statusText = hasTrial ? "Trial mode" : "Usage mode";
  const countdownText = hasTrial ? daysText : usageText;

  useLayoutEffect(() => {
    if (!showUnlockButton) return;
    syncUnlockButtonWidth(unlockButtonRef.current, step);
  }, [
    showUnlockButton,
    step,
    statusText,
    countdownText,
    usageResetText,
    rotating,
    hasUsage,
    showPromo,
  ]);

  return (
    <div className="header">
      <div className="header__content">
        <div className="header__left_button">
          {showUnlockButton && (
            <button
              type="button"
              ref={unlockButtonRef}
              className={`subscription-status-button subscription-status-button--unlock ${showUsageDepletedUnlock ? "subscription-status-button--unlock-depleted" : ""} subscription-status-button--step-${step}`}
              onClick={hasUsage && !showPromo ? onUsageClick : onUpgradeClick}
            >
              <span className="subscription-status-button__label subscription-status-button__label--default">
                Unlock PRO
              </span>
              {rotating && (
                <span className="subscription-status-button__label subscription-status-button__label--trial-mode">
                  {statusText}
                </span>
              )}
              {rotating && (
                <span className="subscription-status-button__label subscription-status-button__label--trial-days">
                  {countdownText}
                </span>
              )}
              {hasUsage && (
                <span className="subscription-status-button__label subscription-status-button__label--usage-reset">
                  {usageResetText}
                </span>
              )}
              {showPromo && (
                <span className="subscription-status-button__label subscription-status-button__label--promo">
                  {usagePromo.label}
                </span>
              )}
            </button>
          )}
          {upgradeError && (
            <button
              type="button"
              className="header__upgrade-error"
              onClick={onUpgradeClick}
              title="Try opening checkout again"
            >
              {upgradeError}
            </button>
          )}
          {/* Pro, a subscription or a lifetime purchase: Manage plan opens
              the Billing sheet (the logo already says PRO). Step 0 is what
              shows the label. */}
          {isPro && (
            <button
              type="button"
              className={`subscription-status-button subscription-status-button--step-0${billingLoading ? " subscription-status-button--loading" : ""}`}
              disabled={billingLoading}
              aria-busy={billingLoading}
              onClick={onProClick || onManageSubscriptionClick}
            >
              {billingLoading ? (
                <span className="subscription-status-button__loading">
                  <span className="header__spinner" aria-hidden="true" />
                  Loading
                </span>
              ) : (
                <span className="subscription-status-button__label subscription-status-button__label--default">
                  Manage plan
                </span>
              )}
            </button>
          )}
        </div>

        <div
          className="header__title"
          onDoubleClick={(e) => e.metaKey && onDebugToggle()}
        >
          <SkinLogo
            isPro={isPro}
            trialMode={trialMode}
            announcement={announcement}
          />
        </div>

        {showSwitch && (
        <div className="header__right_button">
          {contentApplying ? (
            <span className="header__applying" aria-live="polite">
              <span className="header__spinner" aria-hidden="true" />
              Applying
            </span>
          ) : musicProOnly ? (
            <span className="header__status header__status--muted">
              Pro only
            </span>
          ) : !isSlowedEnabled && mount ? (
            <span className="header__status">Off</span>
          ) : null}
          {offersMini && (
            <MiniButton
              hidden={!isSlowedEnabled}
              onMinimize={onMiniMinimize}
            />
          )}
          <input
            type="checkbox"
            className="switcher"
            checked={isSlowedEnabled}
            // Running out of free minutes must not reach this switch: slowing
            // a video down on regular YouTube is free at any meter reading, and
            // musicProOnly already covers the one place Pro is required to be
            // on at all (YouTube Music). See content/youtube/pro-lock.js.
            disabled={musicProOnly || contentApplying}
            onChange={() => onSlowedToggle(!isSlowedEnabled)}
          />
        </div>
        )}
      </div>

      {showSwitch && <div className="header__bar" />}
    </div>
  );
}
